import React, { useEffect, useState } from 'react';
import { Truck, Loader2, Save, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getDriverVehicle, updateDriverVehicle } from '../services/api';

const blank = () => ({ type: '', make_model: '', load_capacity: '', tail_lift: false, notes: '' });

/**
 * Driver Bidding Profile — up to two vehicles, each with its own details.
 * Customers see these on every bid.
 */
export default function VehicleDetailsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState([]);
  const [maxVehicles, setMaxVehicles] = useState(2);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [vehicles, setVehicles] = useState([blank()]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await getDriverVehicle();
        if (!active) return;
        setOptions(data.options || []);
        setMaxVehicles(data.max_vehicles || 2);
        setNeedsUpdate(!!data.vehicle_update_required);
        setVehicles(data.vehicles?.length ? data.vehicles.map((v) => ({ ...blank(), ...v })) : [blank()]);
      } catch (err) {
        toast.error('Failed to load your vehicle details');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const setField = (i, key, value) =>
    setVehicles((prev) => prev.map((v, idx) => (idx === i ? { ...v, [key]: value } : v)));

  const addVehicle = () => setVehicles((prev) => [...prev, blank()]);
  const removeVehicle = (i) => setVehicles((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const filled = vehicles.filter((v) => v.type);
    if (!filled.length) {
      toast.error('Please choose your main vehicle');
      return;
    }
    setSaving(true);
    try {
      const { data } = await updateDriverVehicle({ vehicles: filled });
      setVehicles(data.vehicles?.length ? data.vehicles.map((v) => ({ ...blank(), ...v })) : [blank()]);
      setNeedsUpdate(false);
      toast.success('Vehicles saved — customers will see these on your bids');
      window.dispatchEvent(new Event('jd:profile-updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vehicle details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-xl border border-slate-200 shadow-sm" data-testid="vehicle-details-panel">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-emerald-50 text-[#2E7D32] border border-emerald-100 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-slate-900">My Vehicles</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Add up to {maxVehicles} vehicles. Customers see these on every bid you place.
            </p>
          </div>
        </div>

        {needsUpdate && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-4"
            data-testid="vehicle-update-required-banner">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-snug">
              <strong>Please update your vehicle.</strong> Motorbikes and bicycles are no longer supported on
              JustDeliver — choose the vehicle you deliver in to keep bidding.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {vehicles.map((v, i) => {
              const taken = vehicles.filter((_, idx) => idx !== i).map((o) => o.type).filter(Boolean);
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`vehicle-card-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#2E7D32]">
                      {i === 0 ? 'Main vehicle' : 'Second vehicle'}
                      {i > 0 && <span className="text-slate-400 font-semibold normal-case tracking-normal"> (optional)</span>}
                    </span>
                    {i > 0 && (
                      <button type="button" onClick={() => removeVehicle(i)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                        data-testid={`remove-vehicle-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-semibold text-slate-600">Vehicle type</Label>
                    <select
                      value={v.type}
                      onChange={(e) => setField(i, 'type', e.target.value)}
                      className="w-full h-11 text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                      data-testid={`vehicle-type-select-${i}`}
                    >
                      <option value="">Choose a vehicle…</option>
                      {options.filter((o) => !taken.includes(o)).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">Make &amp; model <span className="font-normal text-slate-400">(optional)</span></Label>
                      <Input value={v.make_model} onChange={(e) => setField(i, 'make_model', e.target.value)}
                        placeholder="e.g. Ford Transit Custom 2021" maxLength={80} className="rounded-lg"
                        data-testid={`vehicle-make-model-input-${i}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-600">Load capacity <span className="font-normal text-slate-400">(optional)</span></Label>
                      <Input value={v.load_capacity} onChange={(e) => setField(i, 'load_capacity', e.target.value)}
                        placeholder="e.g. 1,200 kg / 10 m³" maxLength={60} className="rounded-lg"
                        data-testid={`vehicle-capacity-input-${i}`} />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/60 cursor-pointer hover:border-[#2E7D32]/40 transition-colors">
                    <input type="checkbox" checked={!!v.tail_lift} onChange={(e) => setField(i, 'tail_lift', e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#2E7D32]" data-testid={`vehicle-tail-lift-checkbox-${i}`} />
                    <span className="text-sm">
                      <span className="font-semibold text-slate-800">Tail lift available</span>
                      <span className="block text-xs text-slate-500">Helpful for heavy or bulky items that can&apos;t be lifted by hand.</span>
                    </span>
                  </label>

                  <div className="space-y-1">
                    <Label className="text-xs font-semibold text-slate-600">Other details <span className="font-normal text-slate-400">(optional)</span></Label>
                    <textarea value={v.notes} onChange={(e) => setField(i, 'notes', e.target.value)} rows={2} maxLength={300}
                      placeholder="e.g. Straps, blankets and trolley carried as standard."
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                      data-testid={`vehicle-notes-input-${i}`} />
                    <p className="text-[11px] text-slate-400">No phone numbers, emails or links.</p>
                  </div>
                </div>
              );
            })}

            {vehicles.length < maxVehicles && (
              <Button variant="outline" onClick={addVehicle}
                className="w-full sm:w-auto rounded-full h-10 px-5 font-semibold gap-2 border-dashed"
                data-testid="add-vehicle-btn">
                <Plus className="w-4 h-4" /> Add a second vehicle
              </Button>
            )}

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-full h-11 px-6 font-bold gap-2 shadow-sm hover:shadow-md transition-all sm:ml-2"
              data-testid="save-vehicle-details-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Vehicles'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
